import InstallmentsPlanTable from 'components/InstallmentsPlanTable';
import PaymentMethodSelect from 'components/PaymentMethodSelect';
import TariffSelect from 'components/TariffSelect';
import { InstallmentsData } from 'constants/Installments';
import { getPriceModel, PriceModel } from 'helpers/price/priceModel';
import { Button, FormInput, FormLabel } from 'modules';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectClientAccess, selectConsultantAccess } from 'stores/onlineForm/onlineForm';
import InstallmentsModal from './InstallmentsModal';

interface PaymentProps {
  values: any;
  errors: any;
  hasClient2: boolean;
  setFieldValue: (field: string, value: any) => void;
  onChange: (e: any) => void;
}

const Payment = ({ values, errors, hasClient2, setFieldValue, onChange }: PaymentProps) => {
  const isClientAccess = useSelector(selectClientAccess);
  const isConsultantAccess = useSelector(selectConsultantAccess);

  const [installmentsModalOpened, setInstallmentsModalOpened] = useState(false);
  const [installmentsData, setInstallmentsData] = useState<InstallmentsData | null>(values?.installmentsData || null);
  const [priceModel, setPriceModel] = useState<PriceModel | null>(null);

  const { productId, paymentMethod, doublePayments, paymentDate, secondPaymentDate, installments, debts, payment } =
    values;

  const { paymentsPlan } = values.installmentsPlan || {};

  const onSubmitInstallments = (data: GQL.IInstallmentsPlan) => {
    onChange({
      target: {
        name: 'installments',
        value: true,
      },
    });
    // TODO: tohoto timeoutu se zbavit a použít multipleChange
    setTimeout(() => {
      onChange({
        target: {
          name: 'installmentsData',
          value: installmentsData,
        },
      });
    }, 300);
    // TODO: tohoto timeoutu se zbavit a použít multipleChange
    setTimeout(() => {
      onChange({
        target: {
          name: 'installmentsPlan',
          value: data,
        },
      });
    }, 600);
    // TODO: tohoto timeoutu se zbavit a použít multipleChange
    setTimeout(() => {
      onChange({
        target: {
          name: 'payment',
          value: data.price,
        },
      });
    }, 900);
    setInstallmentsModalOpened(false);
  };

  const onCloseInstallmentsModal = () => {
    if (!values.installmentsData) {
      onCancelInstallments();
    }
    setInstallmentsModalOpened(false);
  };

  const onCancelInstallments = () => {
    onChange({
      target: {
        name: 'installmentsData',
        value: null,
      },
    });
    // TODO: tohoto timeoutu se zbavit a použít multipleChange
    setTimeout(() => {
      onChange({
        target: {
          name: 'installmentsPlan',
          value: null,
        },
      });
    }, 300);
    // TODO: tohoto timeoutu se zbavit a použít multipleChange
    setTimeout(() => {
      onChange({
        target: {
          name: 'installments',
          value: false,
        },
      });
    }, 600);
    setInstallmentsModalOpened(false);
  };

  const onLeavePayment = () => {
    // min. cena
    if (priceModel?.minimumPrice && priceModel?.minimumPrice > values.payment) {
      setFieldValue('payment', priceModel.minimumPrice);
    }
    // max. cena
    if (priceModel?.maximumPrice && priceModel?.maximumPrice < values.payment) {
      setFieldValue('payment', priceModel.maximumPrice);
    }
  };

  const debtsSum = useMemo(() => debts.reduce((acc: number, debt: any) => (acc += debt.zbyva_uhradit), 0), [debts]);

  const refetchPriceModel = (callback: (response: PriceModel) => void) => {
    getPriceModel({
      productId,
      debtsCount: debts?.length || 0,
      debtsSum,
      paymentType: paymentMethod,
      price: values?.payment,
      dividePayment: doublePayments,
      dateFirstPayment: paymentDate,
      dateSecondPayment: secondPaymentDate,
      installmentsType: installmentsData?.type || '',
      firstPayment: installmentsData?.firstPayment || 0,
      paymentsCount: installmentsData?.paymentsCount || 0,
    }).then(callback);
  };

  useEffect(() => {
    refetchPriceModel((response: PriceModel) => {
      // uložení ceny
      setPriceModel(response);
      // výše první splátky
      if (response.firstPrice && response.firstPrice !== values.payment) {
        setFieldValue('payment', response.firstPrice);
      }
      // výše následné splátky
      if (response.secondPrice && response.secondPrice !== values.secondPayment) {
        setFieldValue('secondPayment', response.secondPrice);
      }
      // min. cena
      if (response.minimumPrice && response.minimumPrice > values.payment) {
        setFieldValue('payment', response.minimumPrice);
      }
      // max. cena
      if (response.maximumPrice && response.maximumPrice < values.payment) {
        setFieldValue('payment', response.maximumPrice);
      }
    });
    // eslint-disable-next-line
  }, [
    productId,
    paymentMethod,
    doublePayments,
    paymentDate,
    secondPaymentDate,
    installmentsData,
    installmentsModalOpened,
    debts,
  ]);

  useEffect(() => {
    refetchPriceModel((response: PriceModel) => {
      // uložení ceny
      setPriceModel(response);
    });
    // eslint-disable-next-line
  }, [payment]);

  useEffect(() => {
    // při změně data platby a nastavených splátkách, se musí přepočítat datumy plateb
    if (
      installments &&
      paymentsPlan &&
      paymentsPlan.length > 0 &&
      priceModel &&
      priceModel?.installments?.paymentsPlan
    ) {
      // datum první platby z plánu neodpovídá datu první platby z formuláře
      if (paymentsPlan[0].date !== paymentDate) {
        // platební plán je spočítán
        if (priceModel?.installments?.paymentsPlan?.length > 0) {
          setFieldValue('installmentsPlan', {
            price: priceModel?.installments?.price,
            paymentsPlan: priceModel?.installments?.paymentsPlan,
          });
        }
      }
    }
    // eslint-disable-next-line
  }, [paymentDate, paymentsPlan, priceModel]);

  useEffect(() => {
    if (priceModel?.canDividePrice === false && values?.doublePayments) {
      setFieldValue('doublePayments', false);
    }
  }, [priceModel?.canDividePrice, values?.doublePayments]);

  return (
    <>
      {isConsultantAccess && (
        <Row>
          <Col>
            <FormInput
              type="checkbox"
              className="mr-5"
              name="doublePayments"
              disabled={priceModel?.canDividePrice === false}
              checked={values?.doublePayments}
              label="Rozdeliť úhradu na 2 platby"
              onChange={onChange}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={12} sm={6} md={4}>
          <TariffSelect
            name="productId"
            label={'Produkt'}
            required
            disabled={isClientAccess || hasClient2}
            value={values?.productId}
            error={errors?.productId}
            onChange={(e) => {
              // při změně produktu se odstraní splátkový plán
              if (values.installments) {
                setInstallmentsData(null);
                onCancelInstallments();
              }
              onChange(e);
            }}
            onLeave={onLeavePayment}
          />
        </Col>
        <Col xs={6} sm={5} md={3}>
          <PaymentMethodSelect
            name="paymentMethod"
            label={'Spôsob úhrady'}
            required={true}
            disabled={isClientAccess}
            value={values?.paymentMethod}
            error={errors?.paymentMethod}
            onChange={onChange}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={6} sm={3} md={3}>
          <FormInput
            type="number"
            name="payment"
            label={values?.doublePayments ? 'Výška 1. platby' : 'Výška platby'}
            required={true}
            disabled={isClientAccess || priceModel?.disablePrices || values?.installments}
            description={
              isConsultantAccess && priceModel?.minimumPrice
                ? `Minimálna výška platby je ${priceModel?.minimumPrice} EUR${
                    priceModel.maximumPrice > priceModel.minimumPrice
                      ? `, a maximálna ${priceModel.maximumPrice} EUR`
                      : ''
                  }`
                : ''
            }
            min={priceModel?.minimumPrice}
            max={priceModel?.maximumPrice}
            value={values?.payment}
            error={errors?.payment}
            style={{ fontWeight: 'bold' }}
            onChange={onChange}
            onLeave={onLeavePayment}
          />
        </Col>
        <Col xs={6} sm={3} md={3}>
          <FormInput
            type="date"
            name="paymentDate"
            label={values?.doublePayments || values?.installments ? 'Datum 1. platby' : 'Datum platby'}
            required={true}
            disabled={isClientAccess}
            value={values?.paymentDate}
            error={errors?.paymentDate}
            onChange={onChange}
          />
        </Col>
        {values?.doublePayments && (
          <>
            <Col xs={6} sm={3} md={3}>
              <FormInput
                type="number"
                name="secondPayment"
                label={'Výška 2. platby'}
                required={true}
                disabled={isClientAccess || priceModel?.disablePrices}
                value={values?.secondPayment}
                error={errors?.secondPayment}
                style={{ fontWeight: 'bold' }}
                onChange={onChange}
              />
            </Col>
            <Col xs={6} sm={3} md={3}>
              <FormInput
                type="date"
                name="secondPaymentDate"
                label={'Datum 2. platby'}
                required={true}
                disabled={isClientAccess}
                value={values?.secondPaymentDate}
                error={errors?.secondPaymentDate}
                onChange={onChange}
              />
            </Col>
          </>
        )}
      </Row>
      {values?.installments && values?.installmentsPlan && (
        <Row>
          <Col md={7} className="mt-1">
            <FormLabel>Prehľad splátok</FormLabel>
            <InstallmentsPlanTable plan={values?.installmentsPlan} />
          </Col>
        </Row>
      )}
      {isConsultantAccess && (
        <Row>
          <Col className="pt-2">
            {(priceModel?.installments.canFixed === true || priceModel?.installments.canEqually === true) &&
              !values?.installments && (
                <Button onClick={() => setInstallmentsModalOpened(true)}>Nastaviť parametre splátok</Button>
              )}
            {values?.installments && (
              <Button variant="danger" onClick={onCancelInstallments}>
                Zrušiť úhradu na splátky
              </Button>
            )}
          </Col>
        </Row>
      )}
      {installmentsModalOpened && priceModel && (
        <InstallmentsModal
          priceModel={priceModel}
          data={installmentsData}
          onChange={setInstallmentsData}
          onSubmit={onSubmitInstallments}
          onClose={onCloseInstallmentsModal}
        />
      )}
    </>
  );
};

export default Payment;
